/**
 * DeployToast
 * ─────────────────────────────────────────────
 * Slides in from the bottom-right when severity='medium'.
 * Auto-dismisses after 8s. Progress bar counts down.
 * Shows version + relative deploy time in Eastern Time.
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatRelative, formatDate, resolveDeployedAt } from '../../constants/changelog';

const { FiZap, FiX, FiArrowRight, FiClock } = FiIcons;

const DURATION_MS = 8000;

const DeployToast = ({ show, message, entry, liveDeployment, onDismiss, onExpand }) => {
  const [progress, setProgress] = useState(100);

  // ── Countdown progress bar ───────────────────────────────
  useEffect(() => {
    if (!show) { setProgress(100); return; }
    const start = Date.now();
    const tick  = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct     = Math.max(0, 100 - (elapsed / DURATION_MS) * 100);
      setProgress(pct);
      if (pct === 0) clearInterval(tick);
    }, 50);
    return () => clearInterval(tick);
  }, [show]);

  // ── Resolve timestamp → display in EST/EDT ───────────────
  const deployedAt  = resolveDeployedAt(entry, liveDeployment);
  const relativeStr = formatRelative(deployedAt);  // "just now", "2h ago"
  const dateStr     = formatDate(deployedAt);       // "Jul 14, 2025"
  const version     = entry?.version || liveDeployment?.version || null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 80, scale: 0.94 }}
          animate={{ opacity: 1, y: 0,  scale: 1    }}
          exit={{   opacity: 0, y: 40,  scale: 0.96 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="fixed bottom-6 right-6 z-[600] w-80 overflow-hidden"
          style={{
            borderRadius: '1.25rem',
            background: 'linear-gradient(160deg, rgba(22,12,36,0.98) 0%, rgba(10,6,18,0.98) 100%)',
            border: '1px solid rgba(255,102,204,0.25)',
            boxShadow: '0 24px 60px rgba(0,0,0,0.7), 0 0 40px rgba(255,102,204,0.08)',
          }}
        >
          {/* Progress bar */}
          <div className="h-0.5 bg-zinc-900 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0"
              style={{
                width: `${progress}%`,
                background: 'linear-gradient(90deg, #ff66cc, #ff6600)',
                transition: 'width 0.05s linear',
              }}
            />
          </div>

          <div className="p-4">
            {/* Header row */}
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-pink/20 to-brand-coral/10 border border-brand-pink/25 flex items-center justify-center flex-shrink-0">
                  <SafeIcon icon={FiZap} className="text-brand-pink text-xs" />
                </div>
                <div>
                  {/* Version pill */}
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <p className="text-[9px] font-black uppercase tracking-widest text-brand-pink">
                      Platform Update
                    </p>
                    {version && (
                      <span className="px-1.5 py-px rounded-full bg-brand-pink/10 border border-brand-pink/20 text-[8px] font-black text-brand-pink">
                        v{version}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-black text-white leading-snug">{message}</p>
                  {/* EST/EDT timestamp */}
                  {(relativeStr || dateStr) && (
                    <div className="flex items-center gap-1 mt-1">
                      <SafeIcon icon={FiClock} className="text-zinc-700 text-[8px]" />
                      <span className="text-[9px] font-bold text-zinc-600">
                        {relativeStr || dateStr}
                        {relativeStr && dateStr && (
                          <span className="text-zinc-800 ml-1">· {dateStr}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={onDismiss}
                className="w-6 h-6 rounded-lg flex items-center justify-center text-zinc-600 hover:text-white hover:bg-white/8 transition-all flex-shrink-0 mt-0.5"
              >
                <SafeIcon icon={FiX} className="text-xs" />
              </button>
            </div>

            {/* Actions */}
            <div className="flex gap-2 mt-3">
              <button
                onClick={onDismiss}
                className="flex-1 py-2 rounded-xl border border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700 transition-all text-[10px] font-black uppercase tracking-widest"
              >
                Dismiss
              </button>
              <button
                onClick={onExpand}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 btn-brand rounded-xl text-[10px]"
              >
                View Changes <SafeIcon icon={FiArrowRight} className="text-[10px]" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DeployToast;